import React, { useEffect, useState } from "react";
import axios from "axios";
import { Users, UserCheck, UserX, RefreshCw, Download, Lock, LogOut, Mail, Plus, Copy, Trash2, Link2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const C = { primary: "#5C162E", secondary: "#C7386B", accent: "#D4AF37", text: "#4A0E22", bg: "#FBEEF0" };

export default function Guests() {
  const { user, isPublisher, login, logout } = useAuth();
  const eventKey = window.location.pathname.startsWith("/walimah") ? "walimah" : "main";
  const eventBase = eventKey === "walimah" ? "/walimah" : "";
  const eventLabel = eventKey === "walimah" ? "Walimah" : "Wedding";
  const q = `?eventKey=${eventKey}`;
  const [rsvps, setRsvps] = useState([]);
  const [loading, setLoading] = useState(false);
  const [emailing, setEmailing] = useState(false);
  const [invites, setInvites] = useState([]);
  const [newGuest, setNewGuest] = useState("");
  const [adding, setAdding] = useState(false);

  const emailSummary = () => {
    setEmailing(true);
    axios
      .post(`${API}/rsvp/email-summary${q}`, {}, { withCredentials: true })
      .then((res) => toast.success(`Summary emailed to ${res.data.sent_to} (${res.data.responses} responses)`))
      .catch((e) => toast.error(e?.response?.data?.detail || "Could not send email"))
      .finally(() => setEmailing(false));
  };

  const load = () => {
    setLoading(true);
    axios
      .get(`${API}/rsvp${q}`, { withCredentials: true })
      .then((res) => setRsvps(res.data))
      .catch((e) => console.error(e))
      .finally(() => setLoading(false));
    axios
      .get(`${API}/invites${q}`, { withCredentials: true })
      .then((res) => setInvites(res.data))
      .catch(() => {});
  };

  const addGuest = () => {
    const name = newGuest.trim();
    if (!name) return;
    setAdding(true);
    axios
      .post(`${API}/invites`, { guestName: name, eventKey }, { withCredentials: true })
      .then((res) => {
        setInvites((prev) => [res.data, ...prev]);
        setNewGuest("");
        toast.success(`Link created for ${res.data.guestName}`);
      })
      .catch(() => toast.error("Could not create link"))
      .finally(() => setAdding(false));
  };

  const deleteInvite = (slug) => {
    axios
      .delete(`${API}/invites/${slug}${q}`, { withCredentials: true })
      .then(() => setInvites((prev) => prev.filter((i) => i.slug !== slug)))
      .catch(() => toast.error("Could not delete"));
  };

  const inviteUrl = (slug) => `${window.location.origin}${eventBase}/i/${slug}`;

  const copyLink = (slug) => {
    const url = inviteUrl(slug) + "?utm_source=share";
    navigator.clipboard.writeText(url).then(
      () => toast.success("Link copied!"),
      () => toast.error("Copy failed — long-press to copy: " + url)
    );
  };

  useEffect(() => {
    if (isPublisher) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPublisher]);

  if (user === undefined) {
    return <div className="min-h-screen flex items-center justify-center" style={{ background: C.bg, color: C.primary, fontFamily: "'Cormorant Garamond', serif", fontSize: 24 }}>Loading…</div>;
  }

  if (!isPublisher) {
    return (
      <div className="min-h-screen flex items-center justify-center px-6" style={{ background: C.bg }}>
        <div className="w-full max-w-sm text-center flex flex-col gap-5 rounded-3xl bg-white/70 backdrop-blur-xl p-8 shadow-xl" style={{ border: `1px solid ${C.primary}22` }} data-testid="guests-gate">
          <Lock className="mx-auto" size={30} style={{ color: C.primary }} />
          <h1 className="text-3xl" style={{ color: C.primary, fontFamily: "'Cormorant Garamond', serif" }}>Guest List</h1>
          <p className="text-sm text-neutral-500">Please sign in as the publisher to view RSVP responses.</p>
          <button data-testid="guests-login-btn" onClick={login} className="rounded-xl py-3 text-white font-body tracking-wide text-sm" style={{ background: C.primary }}>Sign in with Google</button>
          <a href="/" className="text-xs text-neutral-500 underline">← Back to invitation</a>
        </div>
      </div>
    );
  }

  const totalResponses = rsvps.length;
  const attending = rsvps.filter((r) => r.attending);
  const declined = rsvps.filter((r) => !r.attending);
  const totalGuests = attending.reduce((sum, r) => sum + (Number(r.guests) || 0), 0);
  const totalMen = attending.reduce((sum, r) => sum + (Number(r.men) || 0), 0);
  const totalWomen = attending.reduce((sum, r) => sum + (Number(r.women) || 0), 0);

  const exportCsv = () => {
    const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const rows = [
      ["Name", "Invited Via", "Men", "Women", "Total Guests", "Attending", "Message", "Date"],
      ...rsvps.map((r) => [r.name, r.invitedName || "", r.men ?? 0, r.women ?? 0, r.guests, r.attending ? "Yes" : "No", r.message || "", new Date(r.createdAt).toLocaleString()]),
    ];
    const csv = rows.map((row) => row.map(esc).join(",")).join("\n");
    const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `guest-list-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const Stat = ({ icon, label, value, testid }) => (
    <div className="flex-1 rounded-2xl bg-white/70 backdrop-blur-xl p-5 text-center shadow-sm" style={{ border: `1px solid ${C.primary}18` }} data-testid={testid}>
      <div className="flex justify-center mb-2" style={{ color: C.secondary }}>{icon}</div>
      <p className="font-display text-4xl" style={{ color: C.primary }}>{value}</p>
      <p className="text-[11px] tracking-widest uppercase text-neutral-500 mt-1">{label}</p>
    </div>
  );

  return (
    <div className="min-h-screen px-5 sm:px-8 py-10" style={{ background: C.bg, color: C.text }} data-testid="guests-page">
      <div className="mx-auto max-w-3xl">
        <div className="flex items-center justify-between mb-8">
          <div>
            <div className="flex items-center gap-2">
              <h1 className="font-display text-4xl" style={{ color: C.primary }}>Guest List</h1>
              <span className="text-[10px] font-body tracking-widest uppercase px-2.5 py-1 rounded-full" style={{ background: C.primary, color: "#fff" }} data-testid="guests-event-badge">{eventLabel}</span>
            </div>
            <p className="text-sm text-neutral-500 mt-1">
              Responses for your <strong>{eventLabel.toLowerCase()}</strong> invitation.{" "}
              <a href={eventKey === "walimah" ? "/guests" : "/walimah/guests"} className="underline" style={{ color: C.secondary }} data-testid="guests-switch-link">
                View {eventKey === "walimah" ? "Wedding" : "Walimah"} list →
              </a>
            </p>
          </div>
          <div className="flex items-center gap-2 flex-wrap justify-end">
            <button onClick={emailSummary} disabled={emailing || totalResponses === 0} data-testid="guests-email-summary-btn" className="flex items-center gap-2 rounded-full px-4 py-2 text-xs shadow-sm text-white disabled:opacity-40" style={{ background: C.secondary }}>
              <Mail size={14} /> {emailing ? "Sending…" : "Email me the list"}
            </button>
            <button onClick={exportCsv} disabled={totalResponses === 0} data-testid="guests-export-btn" className="flex items-center gap-2 rounded-full px-4 py-2 text-xs shadow-sm text-white disabled:opacity-40" style={{ background: C.primary }}>
              <Download size={14} /> Export CSV
            </button>
            <button onClick={load} data-testid="guests-refresh-btn" className="flex items-center gap-2 rounded-full px-4 py-2 text-xs shadow-sm bg-white/70" style={{ color: C.primary, border: `1px solid ${C.primary}22` }}>
              <RefreshCw size={14} className={loading ? "animate-spin" : ""} /> Refresh
            </button>
            <button onClick={logout} data-testid="guests-logout-btn" className="flex items-center gap-2 rounded-full px-4 py-2 text-xs shadow-sm bg-white/70" style={{ color: C.primary, border: `1px solid ${C.primary}22` }}>
              <LogOut size={14} /> Logout
            </button>
          </div>
        </div>

        <div className="flex gap-3 sm:gap-4 mb-8">
          <Stat icon={<Users size={22} />} label="Total attending" value={totalGuests} testid="stat-total-guests" />
          <Stat icon={<UserCheck size={22} />} label="Men / Women" value={`${totalMen} / ${totalWomen}`} testid="stat-men-women" />
          <Stat icon={<UserX size={22} />} label="Regrets" value={declined.length} testid="stat-declined" />
        </div>

        {/* Per-guest invitation links */}
        <div className="rounded-2xl bg-white/70 backdrop-blur-xl shadow-sm mb-8 p-5" style={{ border: `1px solid ${C.primary}18` }} data-testid="invites-section">
          <div className="flex items-center gap-2 mb-1">
            <Link2 size={18} style={{ color: C.secondary }} />
            <h2 className="font-display text-2xl" style={{ color: C.primary }}>Personalized guest links</h2>
          </div>
          <p className="text-sm text-neutral-500 mb-4">Create a unique link for each guest. The invitation greets them by name and their RSVP is tagged to them automatically.</p>
          <div className="flex gap-2 mb-4">
            <input
              data-testid="invite-name-input"
              value={newGuest}
              onChange={(e) => setNewGuest(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && addGuest()}
              placeholder="Guest name (e.g. Mohammad Farooq)"
              dir="auto"
              className="flex-1 rounded-lg px-4 py-2.5 text-sm outline-none bg-white/70"
              style={{ border: `1px solid ${C.primary}33`, color: C.text }}
            />
            <button onClick={addGuest} disabled={adding || !newGuest.trim()} data-testid="invite-add-btn" className="flex items-center gap-2 rounded-lg px-4 py-2.5 text-sm text-white disabled:opacity-40" style={{ background: C.primary }}>
              <Plus size={16} /> Create link
            </button>
          </div>
          {invites.length === 0 ? (
            <p className="text-sm text-neutral-400" data-testid="invites-empty">No guest links yet. Add a name above to create one.</p>
          ) : (
            <div className="flex flex-col divide-y" style={{ borderColor: `${C.primary}12` }} data-testid="invites-list">
              {invites.map((inv) => (
                <div key={inv.slug} className="flex items-center gap-3 py-3" data-testid={`invite-row-${inv.slug}`}>
                  <div className="flex-1 min-w-0">
                    <p dir="auto" className="font-display text-lg truncate" style={{ color: C.text }}>{inv.guestName}</p>
                    <p className="text-[11px] text-neutral-400 truncate">{inviteUrl(inv.slug)}</p>
                  </div>
                  <button onClick={() => copyLink(inv.slug)} data-testid={`invite-copy-${inv.slug}`} className="flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs text-white shrink-0" style={{ background: C.secondary }}>
                    <Copy size={13} /> Copy
                  </button>
                  <button onClick={() => deleteInvite(inv.slug)} data-testid={`invite-delete-${inv.slug}`} className="rounded-full p-2 shrink-0 bg-white/70" style={{ color: "#c0455f", border: `1px solid ${C.primary}22` }}>
                    <Trash2 size={14} />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {totalResponses === 0 ? (
          <div className="rounded-2xl bg-white/60 p-10 text-center text-neutral-500" data-testid="guests-empty">No responses yet.</div>
        ) : (
          <div className="rounded-2xl overflow-hidden bg-white/70 backdrop-blur-xl shadow-sm" style={{ border: `1px solid ${C.primary}18` }} data-testid="guests-table">
            {rsvps.map((r, idx) => (
              <div key={r.id} className="flex items-center gap-4 px-5 py-4" style={{ borderTop: idx === 0 ? "none" : `1px solid ${C.primary}12` }} data-testid={`guest-row-${idx}`}>
                <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ background: r.attending ? "#3f9c5a" : "#c0455f" }} />
                <div className="flex-1 min-w-0">
                  <p dir="auto" className="font-display text-xl truncate" style={{ color: C.text }}>{r.name}</p>
                  {r.invitedName && <p className="text-[11px] truncate" style={{ color: C.secondary }}>via {r.invitedName}'s link</p>}
                  {r.message && <p dir="auto" className="text-xs text-neutral-500 truncate italic">"{r.message}"</p>}
                </div>
                <div className="text-right shrink-0">
                  <p className="text-sm font-body" style={{ color: r.attending ? C.primary : "#c0455f" }}>{r.attending ? `Attending · ${r.guests}` : "Not attending"}</p>
                  {r.attending && (Number(r.men) > 0 || Number(r.women) > 0) && (
                    <p className="text-[11px] text-neutral-500">{Number(r.men) || 0} men · {Number(r.women) || 0} women</p>
                  )}
                  <p className="text-[10px] text-neutral-400">{new Date(r.createdAt).toLocaleDateString()}</p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
